import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useCompaniesOptional } from '../../context/CompanyContext';
import {
  fetchTeamMembers,
  createTeamMember,
  updateTeamMember,
  removeTeamMember,
} from '../../api/teamMembers';
import CompanyMemberHubupQrModal from '../../components/hubup/CompanyMemberHubupQrModal';

const roleOptions = [
  { value: 'utilisateur', label: 'Utilisateur' },
  { value: 'team', label: 'Responsable équipe' },
];

const TeamManagement = () => {
  const companyCx = useCompaniesOptional();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [qrUser, setQrUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    permission: 'utilisateur',
  });

  const loadMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTeamMembers();
      setUsers(res?.data?.users || []);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les membres de l'équipe.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [companyCx?.activeCompanyId]);

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', permission: 'utilisateur' });
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      permission: user.permission === 'team' ? 'team' : 'utilisateur',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    try {
      const payload = {
        email: formData.email,
        permission: formData.permission,
      };
      if (!editingUser || formData.password) {
        payload.password = formData.password;
      }

      if (editingUser) {
        const res = await updateTeamMember(editingUser._id, payload);
        const updated = res?.data?.user;
        if (updated) {
          setUsers((prev) => prev.map((u) => (u._id === updated._id ? updated : u)));
        }
      } else {
        const res = await createTeamMember(payload);
        const created = res?.data?.user;
        if (res?.data?.linkedExisting) {
          setInfo('Ce compte existait déjà et a été ajouté à votre entreprise.');
        }
        if (created) {
          setUsers((prev) => {
            const exists = prev.some((u) => u._id === created._id);
            if (exists) {
              return prev.map((u) => (u._id === created._id ? created : u));
            }
            return [created, ...prev];
          });
        } else {
          await loadMembers();
        }
      }
      setShowForm(false);
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la sauvegarde du membre.");
    }
  };

  const handleRemove = async (user) => {
    const email = user?.email || 'ce membre';
    if (
      !window.confirm(`Retirer ${email} de votre entreprise ?`)
    ) {
      return;
    }
    try {
      await removeTeamMember(user._id);
      setUsers((prev) => prev.filter((u) => u._id !== user._id));
    } catch (e) {
      console.error(e);
      setError('Erreur lors du retrait du membre.');
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleLabel = (value) =>
    roleOptions.find((p) => p.value === value)?.label || value;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Gestion d&apos;équipe - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <FilterPanel />

      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Gestion d&apos;équipe
              </h1>
            </div>
            <Button
              onClick={openCreate}
              iconName="UserPlus"
              iconPosition="left"
              size="lg"
            >
              Ajouter un membre
            </Button>
          </div>

          {error && <p className="text-xs text-error mb-2">{error}</p>}
          {info && <p className="text-xs text-success mb-2">{info}</p>}
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Membres
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Dernière connexion
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Créé le
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                      colSpan={5}
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                      colSpan={5}
                    >
                      Aucun membre pour cette entreprise.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user._id} className="hover:bg-muted/50 transition-smooth">
                      <td className="px-4 py-3 text-sm text-foreground">
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-muted transition-smooth shrink-0"
                            title="QR HubUP"
                            onClick={() => setQrUser(user)}
                          >
                            <Icon name="QrCode" size={16} className="text-muted-foreground" />
                          </button>
                          <span className="truncate">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {getRoleLabel(user.permission)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(user.lastLogin)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Modifier"
                            onClick={() => openEdit(user)}
                          >
                            <Icon name="Pencil" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Retirer de l'entreprise"
                            onClick={() => handleRemove(user)}
                          >
                            <Icon name="UserMinus" size={16} className="text-error" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">
                {editingUser ? 'Modifier un membre' : 'Ajouter un membre'}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={() => setShowForm(false)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Mot de passe{' '}
                  {editingUser && (
                    <span className="text-muted-foreground">
                      (laisser vide pour ne pas changer)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, password: e.target.value }))
                  }
                  placeholder={
                    editingUser ? 'Laisser vide pour conserver le mot de passe' : ''
                  }
                  {...(!editingUser ? { required: true } : {})}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Rôle dans le CRM
                </label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.permission}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, permission: e.target.value }))
                  }
                >
                  {roleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="success" iconName="Save" iconPosition="left">
                  Sauvegarder
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {qrUser ? (
        <CompanyMemberHubupQrModal user={qrUser} onClose={() => setQrUser(null)} />
      ) : null}
    </div>
  );
};

export default TeamManagement;
